import * as Rx from 'rxjs';
import { debounceTime, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import document from 'v2/components/Shared/Masks/document';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { IWhiteList, IFilter } from 'v2/interfaces/whitelist';

import apiService from './api';
import type { ApiService } from './api';

export const InitialFilter: IFilter = { type: 'customer_name' };

export const InitialWhiteList: IWhiteList = {
  customer_name: '',
  customer_email: '',
  customer_document: ''
};

const InitialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 10
};

class WhiteListService {
  private loadingExporting$ = new Rx.BehaviorSubject<boolean>(false);

  private filters$ = new Rx.BehaviorSubject<IFilter>(InitialFilter);

  private loadingWhiteLists$ = new Rx.BehaviorSubject<boolean>(true);
  private whitelists$ = new Rx.BehaviorSubject<Array<IWhiteList>>([]);

  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private ordering$ = new Rx.BehaviorSubject<boolean | 'asc' | 'desc'>(false);
  private ordered$ = new Rx.BehaviorSubject<number>(-1);

  private currentWhiteList$ = new Rx.BehaviorSubject<IWhiteList>(InitialWhiteList);

  private createAndEditDialog$ = new Rx.BehaviorSubject<boolean>(false);

  constructor(private apiService: ApiService) {}

  public loadingExporting = () => this.loadingExporting$;

  public setFilter(filter: IFilter): void {
    this.loadingWhiteLists$.next(true);

    this.filters$.pipe(take(1)).subscribe(() => this.filters$.next(filter));
  }

  public getOrdering = (): Rx.Observable<boolean | 'asc' | 'desc'> => this.ordering$;

  public setOrdering(ordering: boolean | 'asc' | 'desc'): void {
    this.ordering$.next(ordering);
  }

  public getOrdered = (): Rx.Observable<number> => this.ordered$;

  public setOrdered(ordered: number): void {
    this.ordered$.next(ordered);
  }

  public getFilters = (): Rx.Observable<IFilter> => this.filters$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public isLoading = () => this.loadingWhiteLists$;

  public getWhiteLists = (): Rx.Observable<Array<IWhiteList>> => this.whitelists$;

  public updateListWhiteLists = () => {
    this.loadingWhiteLists$.next(true);

    this.getFilters()
      .pipe(
        switchMap(filters => this.getPaginationParams().pipe(map(params => ({ ...filters, ...params })))),
        switchMap(filters => this.apiService.get('whitelist', filters)),
        tap(response => this.whitelists$.next(response.data)),
        tap(response => this.paginationResponse$.next(response.pagination)),
        tap(() => this.loadingWhiteLists$.next(false)),
        take(1)
      )
      .subscribe();
  };

  public loadWhiteLists() {
    return this.getFilters().pipe(
      switchMap(filters => this.resetPage().pipe(map(params => ({ ...filters, ...params })))),
      debounceTime(10),
      switchMap(filters => {
        this.loadingWhiteLists$.next(true);
        return this.apiService.get('whitelist', filters).pipe(finalize(() => this.loadingWhiteLists$.next(false)));
      }),
      tap(response => this.whitelists$.next(response.data)),
      tap(response => this.paginationResponse$.next(response.pagination))
    );
  }

  public getCurrentWhiteList = (): Rx.Observable<IWhiteList> => this.currentWhiteList$;

  public setCurrentWhiteList = (whiteList: IWhiteList | null) => {
    if (!whiteList) {
      this.currentWhiteList$.next(InitialWhiteList);
      return;
    }

    const wl: IWhiteList = {
      ...whiteList,
      customer_document: document.clean(String(whiteList.customer_document ?? ''))
    };
    this.currentWhiteList$.next(wl);
  };

  public getCreateAndEditDialog = (): Rx.Observable<boolean> => this.createAndEditDialog$;

  public setCreateAndEditDialog = (open: boolean) => this.createAndEditDialog$.next(open);

  public saveOrUpdate = (whiteList: IWhiteList) => {
    if (whiteList?.id) {
      return this.apiService.put(`whitelist/${whiteList.id}`, whiteList).pipe(tap(() => this.updateListWhiteLists()));
    }

    return this.apiService.post('whitelist', whiteList).pipe(tap(() => this.updateListWhiteLists()));
  };

  public delete = (whiteList: IWhiteList) => {
    return this.apiService.delete(`whitelist/${whiteList.id}`, whiteList).pipe(tap(() => this.updateListWhiteLists()));
  };

  public cleanFilter() {
    this.filters$.next(InitialFilter);
  }
}

const whitelistService = new WhiteListService(apiService);
export default whitelistService;
